import type { ApiSessionData } from '../api/types';

import {
  DEBUG,
  SESSION_USER_KEY,
} from '../config';
import generateUniqueId from './generateUniqueId';
import {
  ACCOUNT_INFO_CHANGED_EVENT,
  ACCOUNT_SLOT,
  getAccountSlots,
  getGlobalStateCacheKey,
  loadSlotSession,
  storeAccountData,
} from './multiaccount';
import {
  decryptStoredSession,
  encryptStoredSession,
  isEncryptedStoredSession,
} from './sessionEncryption';

type StoredSession = NonNullable<ReturnType<typeof loadSlotSession>>;
type StoreSessionOptions = {
  shouldPersistSessionSecrets?: boolean;
};

const DC_IDS = [1, 2, 3, 4, 5];
let memorySession: (ApiSessionData & { userId?: string }) | undefined;
let sessionWriteGeneration = 0;
let pendingSessionWrite: Promise<boolean> | undefined;

export function hasStoredSession() {
  if (checkSessionLocked()) return true;

  if (memorySession) return true;

  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (slotData?.dcId && (isEncryptedStoredSession(slotData.sessionEncrypted) || hasPlainSessionKeys(slotData))) {
    return true;
  }

  if (!ACCOUNT_SLOT) {
    const legacyAuthJson = localStorage.getItem(SESSION_USER_KEY);
    if (!legacyAuthJson) return false;

    try {
      const userAuth = JSON.parse(legacyAuthJson);
      return Boolean(userAuth && userAuth.id && userAuth.dcID && hasLegacySessionKeys());
    } catch {
      return false;
    }
  }

  return false;
}

export function storeSession(
  sessionData: ApiSessionData,
  currentUserId?: string,
  options: StoreSessionOptions = {},
) {
  const { shouldPersistSessionSecrets = true } = options;
  const { mainDcId, isTest } = sessionData;
  const slot = ACCOUNT_SLOT || 1;
  const currentSlotData = loadSlotSession(slot);
  const userId = currentUserId || currentSlotData?.userId;
  const generation = ++sessionWriteGeneration;

  memorySession = {
    ...sessionData,
    userId,
  };

  const slotData: StoredSession = {
    ...(shouldPersistSessionSecrets
      ? omitPlainSessionSecrets(currentSlotData) : omitAllSessionSecrets(currentSlotData)),
    dcId: mainDcId,
    userId,
    isTest: isTest ?? currentSlotData?.isTest,
    sessionRevision: generateUniqueId(),
  };

  // Keep the last usable session until encryption completes, including during legacy migration.
  const isMigrating = shouldPersistSessionSecrets && hasPlainSessionKeys(currentSlotData);
  if (!isMigrating) {
    localStorage.setItem(`account${slot}`, JSON.stringify(slotData));
  }

  if (shouldPersistSessionSecrets) {
    pendingSessionWrite = persistEncryptedSession(
      slot, sessionData, generation, isMigrating ? currentSlotData! : slotData,
    );
    return pendingSessionWrite;
  } else if (!ACCOUNT_SLOT) {
    clearStoredLegacySession();
  }

  pendingSessionWrite = undefined;
  return Promise.resolve(true);
}

export async function flushStoredSession() {
  while (pendingSessionWrite) {
    const pending = pendingSessionWrite;
    const isSaved = await pending;
    if (pendingSessionWrite === pending) return isSaved;
  }
  return true;
}

export function clearStoredSession(slot = ACCOUNT_SLOT, shouldKeepAccountInfo = false) {
  if ((slot || 1) === (ACCOUNT_SLOT || 1)) {
    memorySession = undefined;
    sessionWriteGeneration += 1;
    pendingSessionWrite = undefined;
  }
  const slotToClear = slot || 1;
  if (shouldKeepAccountInfo) {
    clearStoredSessionSecrets(slot);
  } else {
    localStorage.removeItem(`account${slotToClear}`);
  }

  if (!slot) {
    clearStoredLegacySession();
  }
}

export function clearAllStoredSessions() {
  memorySession = undefined;
  sessionWriteGeneration += 1;
  pendingSessionWrite = undefined;
  clearStoredLegacySession();
  getAccountSlots().forEach((slot) => localStorage.removeItem(`account${slot}`));
}

export function clearStoredSessionSecrets(slot = ACCOUNT_SLOT) {
  if ((slot || 1) === (ACCOUNT_SLOT || 1)) {
    sessionWriteGeneration += 1;
    pendingSessionWrite = undefined;
  }
  const slotToClear = slot || 1;
  const slotData = loadSlotSession(slotToClear);
  if (slotData) {
    localStorage.setItem(`account${slotToClear}`, JSON.stringify(omitAllSessionSecrets(slotData)));
  }

  if (!slot) {
    clearStoredLegacySession();
  }
}

function clearStoredSessionPlainSecrets(slot = ACCOUNT_SLOT) {
  const slotToClear = slot || 1;
  const slotData = loadSlotSession(slotToClear);
  if (slotData) {
    localStorage.setItem(`account${slotToClear}`, JSON.stringify(omitPlainSessionSecrets(slotData)));
  }

  if (!slot) {
    clearStoredLegacySession();
  }
}

function clearStoredLegacySession() {
  [
    SESSION_USER_KEY,
    'dc',
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => localStorage.removeItem(key));
}

export async function loadStoredSession(): Promise<ApiSessionData | undefined> {
  if (memorySession) {
    const { userId, ...sessionData } = memorySession;
    return sessionData;
  }

  if (checkSessionLocked()) return undefined;
  if (!hasStoredSession()) return undefined;

  const slotData = loadSlotSession(ACCOUNT_SLOT);
  if (slotData) {
    if (isEncryptedStoredSession(slotData.sessionEncrypted)) {
      const generation = sessionWriteGeneration;
      const session = await decryptStoredSession(slotData.sessionEncrypted);
      if (!session?.mainDcId || !Object.keys(session.keys || {}).length) {
        throw new Error('Invalid encrypted session');
      }
      const currentSlotData = loadSlotSession(ACCOUNT_SLOT);
      if (generation !== sessionWriteGeneration || currentSlotData?.sessionEncrypted !== slotData.sessionEncrypted) {
        return loadStoredSession();
      }
      memorySession = { ...session, userId: currentSlotData.userId };
      clearStoredSessionPlainSecrets(ACCOUNT_SLOT);
      return session;
    }

    const plainSession = readPlainSlotSession(slotData);
    if (plainSession) {
      await storeSession(plainSession, slotData.userId);
      if (!loadSlotSession(ACCOUNT_SLOT)) memorySession = undefined;
      return memorySession ? loadStoredSession() : undefined;
    }

    if (ACCOUNT_SLOT) return undefined;
  }

  if (ACCOUNT_SLOT) return undefined;

  const legacySession = readLegacySession();
  if (!legacySession) return undefined;

  await storeSession(legacySession.session, legacySession.userId);
  if (!loadSlotSession(ACCOUNT_SLOT)) memorySession = undefined;
  return memorySession ? loadStoredSession() : undefined;
}

function readPlainSlotSession(slotData: StoredSession): ApiSessionData | undefined {
  const keys: Record<number, string | number[]> = {};
  const hashes: Record<number, string | number[]> = {};

  DC_IDS.forEach((dcId) => {
    const key = slotData[`dc${dcId}_auth_key` as keyof StoredSession];
    const hash = slotData[`dc${dcId}_hash` as keyof StoredSession];
    if (key) keys[dcId] = key as string | number[];
    if (hash) hashes[dcId] = hash as string | number[];
  });

  if (!Object.keys(keys).length) return undefined;

  return {
    mainDcId: slotData.dcId,
    keys,
    hashes,
    isTest: slotData.isTest,
  };
}

function readLegacySession(): { session: ApiSessionData; userId?: string } | undefined {
  const userAuthJson = localStorage.getItem(SESSION_USER_KEY);
  if (!userAuthJson) return undefined;

  try {
    const userAuth = JSON.parse(userAuthJson);
    const mainDcId = Number(userAuth.dcID);
    const keys: Record<number, string | number[]> = {};
    const hashes: Record<number, string | number[]> = {};

    DC_IDS.forEach((dcId) => {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      const hash = localStorage.getItem(`dc${dcId}_hash`);
      if (key) keys[dcId] = JSON.parse(key);
      if (hash) hashes[dcId] = JSON.parse(hash);
    });

    if (!Object.keys(keys).length) {
      clearStoredLegacySession();
      return undefined;
    }

    return {
      session: {
        mainDcId,
        keys,
        hashes,
        isTest: userAuth.test,
      },
      userId: userAuth.id,
    };
  } catch (err) {
    warnSessionStorageError('Failed to load stored session', err);
    return undefined;
  }
}

export function updateSessionUserId(currentUserId: string) {
  if (memorySession) {
    memorySession = {
      ...memorySession,
      userId: currentUserId,
    };
  }

  const slot = ACCOUNT_SLOT || 1;
  if (loadSlotSession(slot)) {
    storeAccountData(slot, { userId: currentUserId });
  } else if (!ACCOUNT_SLOT) {
    const legacySession = readLegacySession();
    if (legacySession) {
      storeSession(legacySession.session, currentUserId);
    }
  }
}

function hasPlainSessionKeys(session?: StoredSession) {
  return DC_IDS.some((dcId) => Boolean(session?.[`dc${dcId}_auth_key` as keyof StoredSession]));
}

function hasLegacySessionKeys() {
  return DC_IDS.some((dcId) => Boolean(localStorage.getItem(`dc${dcId}_auth_key`)));
}

function omitPlainSessionSecrets(session?: StoredSession): StoredSession {
  const sanitized = { ...(session || {}) } as StoredSession;
  DC_IDS.forEach((dcId) => {
    delete sanitized[`dc${dcId}_auth_key` as keyof StoredSession];
    delete sanitized[`dc${dcId}_hash` as keyof StoredSession];
  });
  return sanitized;
}

function omitAllSessionSecrets(session?: StoredSession): StoredSession {
  const sanitized = omitPlainSessionSecrets(session);
  delete sanitized.sessionEncrypted;
  delete sanitized.hasSessionData;
  delete sanitized.sessionRevision;
  return sanitized;
}

async function persistEncryptedSession(
  slot: number,
  sessionData: ApiSessionData,
  generation: number,
  previousSlotData: StoredSession,
) {
  try {
    const sessionEncrypted = await encryptStoredSession(sessionData);
    const currentSlotData = loadSlotSession(slot);
    if (generation !== sessionWriteGeneration || !currentSlotData
      || currentSlotData.sessionRevision !== previousSlotData.sessionRevision
      || currentSlotData.sessionEncrypted !== previousSlotData.sessionEncrypted
      || (hasPlainSessionKeys(previousSlotData) && !hasPlainSessionKeys(currentSlotData))) return false;

    const slotData: StoredSession = {
      ...omitPlainSessionSecrets(currentSlotData),
      dcId: sessionData.mainDcId,
      userId: memorySession?.userId || currentSlotData.userId,
      isTest: sessionData.isTest ?? currentSlotData.isTest,
      hasSessionData: true,
      sessionEncrypted,
      sessionRevision: generateUniqueId(),
    };

    localStorage.setItem(`account${slot}`, JSON.stringify(slotData));

    if (!ACCOUNT_SLOT) {
      clearStoredLegacySession();
    }
    window.dispatchEvent(new Event(ACCOUNT_INFO_CHANGED_EVENT));
    return true;
  } catch (err) {
    warnSessionStorageError('Failed to persist encrypted session', err);
    return false;
  }
}

function warnSessionStorageError(message: string, err: unknown) {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.warn(message, err);
  }
}

export function importTestSession() {
  try {
    const sessionData = JSON.parse(process.env.TEST_SESSION!) as ApiSessionData & { userId: string };
    storeSession(sessionData, sessionData.userId);
  } catch (err) {
    warnSessionStorageError('Failed to load test session', err);
  }
}

export function checkSessionLocked() {
  const cache = localStorage.getItem(getGlobalStateCacheKey(ACCOUNT_SLOT));
  try {
    return Boolean(JSON.parse(cache || '{}')?.passcode?.isScreenLocked);
  } catch {
    return false;
  }
}
